import { AnyAction } from 'redux';
// Data layer
import { setRequestStatus } from 'data-layer-cr/dist/network';
import { NetworkResponse } from 'data-layer-cr/dist/network/NetworkTypes';
// Models
import {
  PolicyResumeResponse,
  UpdateVinResponse,
} from 'src/models/responseTypes/PolicyResume';
import {
  EmissionResume,
  makeEmptyEmissionResume,
} from 'src/models/emission/EmissionResume';
import { VinChangeData } from 'src/models/emission/VinChangeData';
import { UpdateVinPayload } from 'src/models/requestTypes/UpdateVin';
// Functions
import { prepareDocumentFromBase64 } from 'src/functions/prepareDocument';
// Redux
import { AsyncAppThunk, AsyncDispatch, AppThunk } from '../../ThunkTypes';
// Network
import processRequest from '../../../services/processRequest';
// Identifiers
import { GET_EMISSION_RESUME, UPDATE_EMISSION_VIN } from '../requestIdentifier';
// actions
import {
  updateEmission,
  setEmissionLoaded,
  resetEmission,
  setNewVin,
  setNewInvoiceName,
  setNewInvoiceBase64,
} from '../slice/emissionSlice';

export const generateEmissionResume = (
  information: PolicyResumeResponse,
): EmissionResume => {
  const { quotation } = information;
  const { summary, policyRequest: emission, customer, user } = quotation;
  const data: EmissionResume = {
    ...makeEmptyEmissionResume(),
    fullname: summary.customerName,
    car: {
      brand: summary.carBrand,
      model: summary.carModel,
      year: summary.carYear,
    },
    vinNumber: emission.unitInformation.serialVin,

    productCoreId: quotation.productCoreId,
    policyNumber: parseInt(quotation.policyNumber, 10),

    customerFullName: `${customer.firstName} ${customer.lastName}`,
    customerEmail: customer.email,
    brokerEmail: user.email,

    id: quotation.id,
    urlSalt: quotation.urlSalt,
    productId: quotation.productId,
    policyId: quotation.policyId,
    operationNumber: quotation.operationNumber,
  };

  const carInvoiceFile = emission.aditionalDocuments.find((doc) =>
    doc.name.startsWith('proforma'),
  );
  if (carInvoiceFile) {
    data.invoiceName = carInvoiceFile.name;
  }

  return data;
};

/**
 * Loads the quotation identifier by `quotationId` to the store.
 *
 * @param quotationId - The id for the specific quotation to load
 */
export const getEmissionPolicyDetailsById =
  (hashQuotation: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(UPDATE_EMISSION_VIN, 'initial'));
    dispatch(setRequestStatus(GET_EMISSION_RESUME, 'requesting'));
    try {
      const response = await processRequest<
        NetworkResponse<PolicyResumeResponse>
      >({
        method: 'GET',
        url: `quotation?hash=${hashQuotation}`,
      });

      dispatch(updateEmission(generateEmissionResume(response.data)));
      dispatch(setEmissionLoaded());

      return dispatch(setRequestStatus(GET_EMISSION_RESUME, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(GET_EMISSION_RESUME, 'failure'));
    }
  };

/**
 * updates emission policy vin in Acsel
 *
 * @param quotationId - The id for the specific quotation to load
 */
export const updateEmissionPolicyVin =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const emissionData = getState().emission.emission;

    const payload: UpdateVinPayload = {
      brokerEmail: emissionData.brokerEmail,
      customerEmail: emissionData.customerEmail,
      customerFullName: emissionData.customerFullName,
      newSerialVIN: emissionData.newVinNumber,
      oldSerialVIN: emissionData.vinNumber,
      policyNumber: emissionData.policyNumber,
      productCoreId: emissionData.productCoreId,
      additionalDocuments: prepareDocumentFromBase64(
        emissionData.newInvoiceBase64,
        'proforma',
      ),
    };

    dispatch(setRequestStatus(UPDATE_EMISSION_VIN, 'requesting'));
    try {
      const response = await processRequest<NetworkResponse<UpdateVinResponse>>(
        {
          method: 'PUT',
          url: `/PolicyEmission/update-vin`,
          data: payload,
        },
      );

      if (response.data.data) {
        return dispatch(setRequestStatus(UPDATE_EMISSION_VIN, 'success'));
      }
      return dispatch(setRequestStatus(UPDATE_EMISSION_VIN, 'failure'));
    } catch (e) {
      return dispatch(setRequestStatus(UPDATE_EMISSION_VIN, 'failure'));
    }
  };

export const updateVinFormValues =
  (data: VinChangeData): AppThunk =>
  (dispatch): void => {
    dispatch(setNewVin(data.vinNumber));
    dispatch(setNewInvoiceName(data.invoiceName));
    dispatch(setNewInvoiceBase64(data.invoiceBase64));
  };

export const cleanEmission =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(resetEmission());
  };
